<template>
  <b-modal
    ref="modalImportFormat"
    id="modalImportFormat"
    :modal-class="$style.container"
    :hide-footer="true"
    :hide-header="true"
    visible
    lazy
    no-fade
    centered
  >
    <div :class="$style.heading">{{ $t('modals.import.title') }}</div>

    <div :class="['form-row mb-2', $style.formats]">
      <div
        v-for="(format) in availableFormats"
        :key="format.format"
        class="col"
      >
        <button
          type="button"
          :class="['btn btn-sm btn-block', selectedFormat === format ? 'btn-secondary' : 'btn-default']"
          @click="selectedFormat = format"
        >
          <img v-if="format.isLocked" src="@/assets/icons/lock.svg" width="9" />
          <span><template v-if="format.isComp && item && item.price > 0">Comp </template>{{ format.text }}</span>
        </button>
      </div>
    </div>

    <div :class="$style.description">{{ showPremiumOptions ? `You have selected premium ${asset}` : selectedFormat.description }}</div>

    <div
      v-if="showPremiumOptions || !item"
      class="custom-select-container my-3"
    >
      <b-form-select v-model="premiumOption" size="sm">
        <option value="subscription">{{ $t('modals.import.deduct') }}</option>
        <option value="comp">Insert Comp</option>
      </b-form-select>
    </div>

    <!-- <div :class="$style.downloadLeft"><span :class="$style.status">27/40</span> downloads remains.</div> -->

    <!-- Show current DND -->
    <div class="my-3">
      <b-form-checkbox
        v-if="item"
        v-model="importDnd"
        size="sm"
      >{{ $t('modals.import.dont_ask_item', { format: selectedFormat.text, price: this.item.price ? 'premium' : 'free', asset: $tc(`items.${asset}.name`, 2) }) }}</b-form-checkbox>
      <!-- Show All DND if user is opening directly -->
      <template v-else>
        <b-form-checkbox
          v-model="importDnd"
          size="sm"
        >{{ $t('modals.import.dont_ask', { price: 'free', asset: $tc(`items.${asset}.name`, 2) }) }}</b-form-checkbox>
        <b-form-checkbox
          v-model="importDndPremium"
          size="sm"
        >{{ $t('modals.import.dont_ask', { price: 'premium', asset: $tc(`items.${asset}.name`, 2) }) }}</b-form-checkbox>
      </template>
    </div>

    <!-- Show Buttons only if there's item -->
    <template v-if="item">
      <template v-if="selectedFormat.isLocked">
        <template v-if="canUserDownload">
          <button
            v-if="isLoggedIn"
            class="btn btn-success btn-block btn-sm"
            type="button"
            @click="onInsert"
          >{{ $t('buttons.confirm_purchase') }}</button>
          <button
            v-else
            class="btn btn-success btn-block btn-sm"
            type="button"
            @click="onLogin"
          >{{ $t('buttons.login_insert') }}</button>
        </template>
        <button
          v-else
          class="btn btn-success btn-block btn-sm"
          type="button"
          @click="onSubscribe"
        >{{ $t('buttons.subscribe_pro') }}</button>
      </template>
      <!-- Show Purchase Options -->
      <button
        v-else
        class="btn btn-primary btn-block btn-sm"
        type="button"
        @click="onInsert"
      >{{ $t('buttons.downloaded') }}</button>
    </template>
    <template v-else>
      <button
        v-if="!selectedFormat.isLocked"
        class="btn btn-primary btn-block btn-sm"
        type="button"
        @click="onClose"
      >{{ $t('buttons.save') }}</button>
      <button
        v-else
        class="btn btn-success btn-block btn-sm"
        type="button"
        @click="onLogin"
      >{{ $t('buttons.login_insert') }}</button>
    </template>

    <!-- <button type="button" class="btn btn-success btn-block btn-sm">Confirm Purchase and Import</button> -->
  </b-modal>
</template>

<script>
import clone from 'lodash/clone'
import Platform from '@/plugin-platforms'
import { webUrl } from '@/lib/route'
import AuthMixin from '@/components/mixins/auth'
import ImportFormatMixin from '@/components/mixins/importFormat'

export default {
  props: {
    asset: {
      type: String,
      default: 'icon'
    },
    item: {
      type: Object,
      default: null
    },
    fn: {
      type: Function,
      default: null
    }
  },

  mixins: [AuthMixin, ImportFormatMixin],

  data () {
    return {
      originalFormat: null
    }
  },

  created () {
    // Set Illustration Format if there's no svg
    if (this.item && this.asset === 'illustration' && !this.hasIllustrationSVG && this.getSelectedFormat(this.asset) === 'svg') {
      this.selectedFormat = {
        format: 'png'
      }
    }

    // If selectedFormat is undefined in any case
    // May be due to past versions in which there was svg available
    if (!this.selectedFormat) {
      this.selectedFormat = {
        format: 'png'
      }
    }
  },

  mounted () {
    this.originalFormat = clone(this.selectedFormat)
  },

  methods: {
    onOpenInWeb () {
      let url = webUrl(this.item.asset, { slug: this.item.slug })
      Platform.openURL(url)
    },
    onInsert () {
      this.fn()
      this.$store.commit('modal/hide')
    },
    onSubscribe () {
      this.selectedFormat = this.originalFormat
      Platform.openURL(webUrl('upgrade'))
      this.$store.commit('modal/hide')
    },
    onLogin () {
      this.selectedFormat = this.originalFormat
      this.$router.push({ name: 'login' })
      this.$store.commit('modal/hide')
    },
    onClose () {
      this.$store.commit('modal/hide')
    }
  }
}
</script>

<style lang="scss" module>
.container {
  color: var(--typo-2);

  :global {
    .modal-dialog {
      max-width: 100%!important;
      margin-left: 15px;
      margin-right: 15px;
      align-items: flex-end !important;
    }
    .modal-body {
      padding: 15px !important;
    }
  }

  .heading {
    color: var(--typo-1);
    font-size: $font-size-lg;
    font-weight: $font-weight-semi-bold;
    margin-bottom: 15px;
  }

  .description {
    font-size: $font-size-sm;
    line-height: 12px;
    margin-bottom: map-get($spacers, 3);
  }

  .formats {
    img {
      vertical-align: baseline;
      margin-right: 5px;
    }
  }

  .downloadLeft {
    color: $typo-light-2;
    font-size: $font-size-sm;
    line-height: 12px;
    background-color: $gray-200;
    margin-top: 10px;
    padding: map-get($spacers, 2) map-get($spacers, 3);

    .status {
      color: $blue;
    }
  }
}
</style>
